import { useState, useEffect } from 'react';
import API_ENDPOINTS from '../Config/api';
import MFAInput from '../Components/MFAInput';
import { useTranslation } from 'react-i18next';

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Pagina di configurazione MFA (attivazione/disattivazione).
 * Accessibile dalla pagina profilo/impostazioni.
 */
export default function MFASetup() {
  const { t } = useTranslation();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('idle'); // idle | setup | confirm | done | disable
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  // disable form
  const [disablePassword, setDisablePassword] = useState('');
  const [disableOtp, setDisableOtp] = useState('');
  const [disableError, setDisableError] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);

  // Fetch MFA status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.mfaStatus, { headers: authHeaders() });
      const json = await res.json();
      setMfaEnabled(json.mfa_enabled || false);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.mfaSetup, {
        method: 'POST',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (res.ok) {
        setQrCode(json.qr_code);
        setSecret(json.secret);
        setStep('setup');
      } else {
        setError(json.detail || 'Errore durante il setup.');
      }
    } catch {
      setError('Errore di rete.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (otpCode) => {
    setError('');
    setConfirmLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.mfaConfirm, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ otp_code: otpCode }),
      });
      const json = await res.json();
      if (res.ok) {
        setBackupCodes(json.backup_codes || []);
        setMfaEnabled(true);
        setStep('done');
      } else {
        setError(json.detail || 'Codice non valido.');
      }
    } catch {
      setError('Errore di rete.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setDisableError('');
    setDisableLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.mfaDisable, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ otp_code: disableOtp, password: disablePassword }),
      });
      const json = await res.json();
      if (res.ok) {
        setMfaEnabled(false);
        setStep('idle');
        setDisablePassword('');
        setDisableOtp('');
      } else {
        setDisableError(json.detail || 'Errore.');
      }
    } catch {
      setDisableError('Errore di rete.');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.mfaBackupCodes, {
        method: 'POST',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (res.ok) {
        setBackupCodes(json.backup_codes || []);
        setStep('done');
      } else {
        setError(json.detail || 'Errore.');
      }
    } catch {
      setError('Errore di rete.');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Caricamento...</div>;
  }

  return (
    <div className="max-w-lg mx-auto py-8 px-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">
        {t('mfa.title', 'Autenticazione a due fattori (MFA)')}
      </h2>
      <p className="text-gray-500 mb-6">
        {t('mfa.description', 'Proteggi il tuo account con Google Authenticator.')}
      </p>

      {/* Status badge */}
      <div className="flex items-center gap-3 mb-6 p-3 rounded-lg bg-gray-50 border">
        <span className={`w-3 h-3 rounded-full ${mfaEnabled ? 'bg-green-500' : 'bg-gray-300'}`} />
        <span className="font-medium text-gray-700">
          {mfaEnabled
            ? t('mfa.statusActive', 'MFA attivo')
            : t('mfa.statusInactive', 'MFA non attivo')}
        </span>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded mb-4">{error}</div>
      )}

      {/* IDLE — show enable/disable buttons */}
      {step === 'idle' && !mfaEnabled && (
        <button
          onClick={handleSetup}
          className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition"
        >
          {t('mfa.enableButton', 'Attiva MFA')}
        </button>
      )}

      {step === 'idle' && mfaEnabled && (
        <div className="space-y-3">
          <button
            onClick={handleRegenerateBackupCodes}
            className="w-full py-2 px-4 bg-gray-600 text-white font-semibold rounded-md hover:bg-gray-700 transition"
          >
            {t('mfa.regenerateBackup', 'Rigenera codici di backup')}
          </button>
          <button
            onClick={() => setStep('disable')}
            className="w-full py-2 px-4 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 transition"
          >
            {t('mfa.disableButton', 'Disattiva MFA')}
          </button>
        </div>
      )}

      {/* SETUP — show QR code */}
      {step === 'setup' && (
        <div className="space-y-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-3">
              {t('mfa.scanQr', 'Scansiona il QR code con Google Authenticator:')}
            </p>
            {qrCode && <img src={qrCode} alt="QR Code MFA" className="mx-auto" />}
            <details className="mt-3 text-sm text-gray-500">
              <summary className="cursor-pointer hover:text-gray-700">
                {t('mfa.manualEntry', 'Inserimento manuale')}
              </summary>
              <code className="block mt-1 p-2 bg-gray-100 rounded text-xs break-all select-all">{secret}</code>
            </details>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2 text-center">
              {t('mfa.enterCode', 'Inserisci il codice per confermare:')}
            </p>
            <MFAInput onComplete={handleConfirm} disabled={confirmLoading} error={error} />
          </div>
          <button
            onClick={() => { setStep('idle'); setError(''); }}
            className="text-sm text-blue-600 hover:underline block text-center w-full"
          >
            {t('common.cancel', 'Annulla')}
          </button>
        </div>
      )}

      {/* DONE — show backup codes */}
      {step === 'done' && backupCodes.length > 0 && (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="font-semibold text-green-800 mb-2">
              {t('mfa.backupTitle', 'Codici di backup')}
            </p>
            <p className="text-sm text-green-700 mb-3">
              {t('mfa.backupInfo', 'Salva questi codici. Ogni codice è utilizzabile una sola volta.')}
            </p>
            <div className="grid grid-cols-2 gap-1 font-mono text-sm">
              {backupCodes.map((code, i) => (
                <span key={i} className="bg-white px-2 py-1 rounded border text-center select-all">{code}</span>
              ))}
            </div>
          </div>
          <button
            onClick={() => { setStep('idle'); setBackupCodes([]); setError(''); }}
            className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition"
          >
            {t('common.done', 'Fatto')}
          </button>
        </div>
      )}

      {/* DISABLE — confirm with OTP + password */}
      {step === 'disable' && (
        <form onSubmit={handleDisable} className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('mfa.disableInfo', 'Per disattivare MFA inserisci la tua password e un codice OTP.')}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={disablePassword}
              onChange={e => setDisablePassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('mfa.otpLabel', 'Codice OTP')}
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={8}
              value={disableOtp}
              onChange={e => setDisableOtp(e.target.value.replace(/\D/g, ''))}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-500"
              required
            />
          </div>
          {disableError && (
            <p className="text-sm text-red-600">{disableError}</p>
          )}
          <button
            type="submit"
            disabled={disableLoading}
            className="w-full py-2 px-4 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 transition disabled:opacity-50"
          >
            {disableLoading ? 'Caricamento...' : t('mfa.confirmDisable', 'Conferma disattivazione')}
          </button>
          <button
            type="button"
            onClick={() => { setStep('idle'); setDisableError(''); }}
            className="text-sm text-blue-600 hover:underline block text-center w-full"
          >
            {t('common.cancel', 'Annulla')}
          </button>
        </form>
      )}
    </div>
  );
}
